<template>
  <section class="confirm-card" :class="{ committed: committed }">
    <p class="confirm-summary">{{ summary }}</p>

    <div v-if="needsPick" class="pick-list">
      <p class="field-label">Which insured?</p>
      <button
        v-for="candidate in candidates"
        :key="candidate.subject_id"
        type="button"
        class="pick-btn"
        @click="choose(candidate)"
      >
        {{ candidate.entity_name }}
        <span v-if="candidate.store_label && candidate.store_label !== candidate.entity_name">
          · {{ candidate.store_label }}
        </span>
      </button>
    </div>

    <p v-if="waiverMissing" class="confirm-error">
      This check has no {{ coverageLabel }} requirement to waive.
    </p>

    <form v-else-if="showForm" class="confirm-form" @submit.prevent="commit">
      <p v-if="pickedName" class="picked-name">{{ pickedName }}</p>

      <label v-if="kind === 'archive'" class="field">
        <span class="field-label">Close date</span>
        <input v-model="closeDate" type="date" class="field-input" />
      </label>

      <label v-if="kind === 'limit'" class="field">
        <span class="field-label">Accepted {{ coverageLabel }} amount</span>
        <input v-model.number="limitAmount" type="number" min="1" class="field-input" />
      </label>

      <label v-if="kind === 'waiver' || kind === 'limit' || kind === 'archive'" class="field">
        <span class="field-label">Reason</span>
        <textarea
          v-model="reason"
          class="field-input"
          rows="2"
          :placeholder="reasonPlaceholder"
        />
      </label>

      <p v-if="error" class="confirm-error">{{ error }}</p>

      <div class="confirm-actions">
        <button type="submit" class="commit-btn" :disabled="!canCommit">
          <i :class="committing ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
          {{ committing ? 'Saving…' : buttonLabel }}
        </button>
      </div>
    </form>

    <p v-if="committed" class="confirm-done">{{ doneMessage }}</p>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import api from '@/services/api'

const props = defineProps({
  action: { type: Object, required: true },
})

const emit = defineEmits(['committed'])

const payload = computed(() => props.action?.payload || {})
const kind = computed(() => payload.value.kind || '')
const candidates = computed(() => payload.value.candidates || [])
const picked = ref(null)
const reason = ref(payload.value.reason || '')
const closeDate = ref(payload.value.close_date || '')
const limitAmount = ref(payload.value.limit_amount || null)
const committing = ref(false)
const committed = ref(false)
const error = ref('')
const doneMessage = ref('')

const active = computed(() => picked.value || (payload.value.ready ? payload.value : null))
const requirements = computed(() => (
  active.value?.requirements || payload.value.requirements || []
))
const needsPick = computed(() => (
  !committed.value
  && !payload.value.ready
  && candidates.value.length > 1
  && !picked.value
  && kind.value !== 'reminder'
))
const franconnectHold = computed(() => Boolean(
  payload.value.franconnect || picked.value?.franconnect
))
const showForm = computed(() => {
  if (franconnectHold.value) return false
  if (committed.value || kind.value === 'reminder') return false
  if (!payload.value.ready && !picked.value) return false
  if (kind.value === 'waiver' && !requirements.value.length) return false
  return Boolean(active.value)
})
const waiverMissing = computed(() => (
  !committed.value
  && kind.value === 'waiver'
  && (payload.value.ready || picked.value)
  && !requirements.value.length
))

const coverageLabel = computed(() => (
  active.value?.coverage_label || payload.value.coverage_label || 'coverage'
))
const pickedName = computed(() => {
  if (!picked.value) return ''
  return picked.value.store_label || picked.value.entity_name
})
const reasonPlaceholder = computed(() => {
  if (kind.value === 'archive') return 'Why is this location closing?'
  if (kind.value === 'waiver') return 'Why does this requirement not apply to this insured?'
  return 'Why is this insured held to a lower limit than the project check?'
})
const summary = computed(() => {
  if (committed.value) return ''
  if (picked.value?.franconnect_summary) return picked.value.franconnect_summary
  return payload.value.summary || ''
})
const buttonLabel = computed(() => {
  if (kind.value === 'limit' && limitAmount.value) {
    const amount = Number(limitAmount.value).toLocaleString('en-US')
    const who = active.value?.store_label || active.value?.entity_name || 'this insured'
    return `Accept $${amount} ${coverageLabel.value} for ${who}`
  }
  return active.value?.button_label || props.action?.label || 'Confirm'
})
const canCommit = computed(() => {
  if (committing.value || committed.value || franconnectHold.value) return false
  if (!reason.value.trim()) return false
  if (kind.value === 'limit' && !(Number(limitAmount.value) > 0)) return false
    if (kind.value === 'waiver') {
      if (!requirements.value.length) return false
    }
  return Boolean(active.value?.subject_id || payload.value.subject_id)
})

const choose = (candidate) => {
  picked.value = {
    ...payload.value,
    ...candidate,
    ready: true,
    requirements: candidate.requirements || payload.value.requirements || [],
  }
  if (kind.value === 'archive') {
    picked.value.button_label = `Archive ${candidate.store_label || candidate.entity_name}`
  }
}

const subjectId = () => active.value?.subject_id || payload.value.subject_id

const commit = async () => {
  if (!canCommit.value) return
  committing.value = true
  error.value = ''
  try {
    let message = ''
    if (kind.value === 'archive') {
      const body = { reason: reason.value.trim() }
      if (closeDate.value) body.close_date = closeDate.value
      const res = await api.post(
        `/compliance-health/entities/${subjectId()}/archive/`,
        body,
      )
      message = res.data?.message || `Archived ${active.value?.entity_name || 'this insured'}.`
    } else if (kind.value === 'waiver') {
      const reqs = requirements.value
      for (const requirement of reqs) {
        await api.post('/compliance-waivers/', {
          compliance_subject: subjectId(),
          requirement_code: requirement.code,
          requirement_name: requirement.name,
          reason: reason.value.trim(),
          scope: 'SUBJECT',
        })
      }
      message = `Waived ${coverageLabel.value} for ${active.value?.entity_name || 'this insured'}.`
    } else if (kind.value === 'limit') {
      const coverage = active.value?.coverage_code || payload.value.coverage_code
      const limitKey = active.value?.limit_key || payload.value.limit_key || 'per_occurrence'
      await api.patch(`/compliance-subjects/${subjectId()}/update_policy/`, {
        policy_minimums: { [coverage]: { [limitKey]: Number(limitAmount.value) } },
        merge: true,
        reason: reason.value.trim(),
      })
      const amount = Number(limitAmount.value).toLocaleString('en-US')
      message = `Accepted $${amount} ${coverageLabel.value} for ${active.value?.entity_name || 'this insured'}.`
    }
    committed.value = true
    doneMessage.value = message
    emit('committed', message)
  } catch (err) {
    error.value = err.response?.data?.error || err.message || 'Could not save this change.'
  } finally {
    committing.value = false
  }
}
</script>

<style scoped>
.confirm-card {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid var(--color-border, #d1d5db);
  border-radius: 10px;
  background: var(--color-surface, #f8fafc);
}
.confirm-summary {
  margin: 0 0 10px;
  font-size: 0.9rem;
  line-height: 1.45;
}
.field-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 4px;
}
.field {
  display: block;
  margin-bottom: 8px;
}
.field-input {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid var(--color-border, #d1d5db);
  border-radius: 6px;
  font: inherit;
}
.pick-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pick-btn,
.commit-btn {
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font: inherit;
}
.pick-btn {
  text-align: left;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid var(--color-border, #d1d5db);
}
.commit-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: var(--color-primary, #1d4ed8);
  color: #fff;
}
.commit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.confirm-error {
  color: #b91c1c;
  font-size: 0.85rem;
}
.confirm-done {
  margin: 0;
  font-weight: 600;
}
.picked-name {
  margin: 0 0 8px;
  font-weight: 600;
}
</style>
